// D-051 (owner 2026-09-30) — the ORDER of a compound walk's persistent-zone entries once the walk is saved, as pure helpers (Codex review #2,
// 2026-09-30). The adapters (websocket/movement-truth.ts, websocket/zone-entry-truth.ts) own the I/O; these decide what runs when.
//   · F1: the mover's entries, then each carried follower's, ONE AFTER ANOTHER — each sees the revisions every earlier commit confirmed (a zone
//     writing its caster saw three entrants on one caster revision and the third answered stale: an effect lost);
//   · F3: a CARRIED follower never halts — a fresh re-resolution after a stale answer keeps every entry of its path (a walker's stops at its halt);
//   · F5: every plan's hand-backs are decided before any real die is drawn.

/** Revisions confirmed so far in one compound's chain; `touched` = members a served entry wrote (their rows changed beyond a position);
 *  `stripped` = status rows an earlier step's end-of-move sweep removed (review #4 F3: a strip moves no revision and reaches the page only by
 *  a later push — a later step's re-resolution must not read them). */
export interface ChainRevs { revs: Map<string, number>; touched: Set<string>; stripped?: Set<string> }
/** One step of the chain: commits with what the earlier steps confirmed, answers what it confirmed (null = nothing / failed). */
export type ChainStep = (confirmed: ChainRevs) => Promise<ChainRevs | null>;

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** The later of two confirmations per member; touched is the union. */
export function mergeChainRevs(a: ChainRevs, b: ChainRevs): ChainRevs {
  const revs = new Map(a.revs);
  for (const [id, rev] of b.revs) if (rev > (revs.get(upper(id)) ?? -Infinity)) revs.set(upper(id), rev);
  const stripped = new Set([...(a.stripped ?? []), ...[...(b.stripped ?? [])].map(upper)]);
  return { revs, touched: new Set([...a.touched, ...[...b.touched].map(upper)]), ...(stripped.size ? { stripped } : {}) };
}

/** F1: run the steps in order, each awaited, carrying the confirmations forward. A step that throws confirms nothing; the chain goes on (a
 *  later follower's entry is still owed). Resolves once every step settled — the caller's holds release then (F2). */
export async function runChain(seed: ChainRevs, steps: readonly ChainStep[]): Promise<ChainRevs> {
  let confirmed: ChainRevs = { revs: new Map([...seed.revs].map(([id, rev]) => [upper(id), rev])), touched: new Set([...seed.touched].map(upper)) };
  for (const step of steps) {
    let out: ChainRevs | null = null;
    try { out = await step(confirmed); } catch { out = null; }
    if (out) confirmed = mergeChainRevs(confirmed, out);
  }
  return confirmed;
}

/** F1: how a step adopts what the chain confirmed. Its own entering creature takes its saved landing revision (the walk moved it only).
 *  The chain's HEAD (no earlier entrant: `head`) was resolved on the page rows; only the walk itself moved since (positions) — it adopts the
 *  confirmed revisions and keeps its verdicts. A LATER step was resolved on rows MODELLED from the earlier entrants' plans, which can differ
 *  from what landed in ways no revision shows (a skipped entry, a sweep, a canonical id, a recompile — review #4 F1–F4): it ALWAYS resolves
 *  again (on its own tapes) on the page rows once they show the confirmed revisions — `refresh` lists every participant with the revision to
 *  wait for. Never a modelled verdict under a passing CAS. */
export function adoptChainRevs(own: ReadonlyMap<string, number>, actorId: string, actorRev: number, participants: Iterable<string>,
  confirmed: ChainRevs | null, head = false): { revs: Map<string, number>; refresh: Map<string, number> } {
  const revs = new Map([...own].map(([id, rev]) => [upper(id), rev]));
  const me = upper(actorId);
  revs.set(me, actorRev);
  const refresh = new Map<string, number>();
  for (const id of new Set([...participants].map(upper))) {
    if (id === me && head) continue;
    const rev = confirmed?.revs.get(id);
    if (head) { if (rev !== undefined) revs.set(id, rev); continue; }
    refresh.set(id, rev ?? revs.get(id) ?? 0);
  }
  if (!head) refresh.set(me, actorRev);
  return { revs, refresh };
}

/** F3: which entries survive a fresh re-resolution from index `from`. `steps[j]` = entry j's foot; `freshHalt` = the foot of the first fresh
 *  verdict that halts the entering creature (undefined = none); `plannedHalt` = the walk's planned halting foot. A walker that now halts
 *  EARLIER drops the entries past that foot (drift); a CARRIED follower keeps them all — it is not walking, nothing stops it. */
export function survivorsAfterReresolve(steps: readonly number[], from: number, freshHalt: number | undefined, plannedHalt: number | undefined,
  carried: boolean): { keep: number[]; drift: boolean } {
  const all = steps.map((_, j) => j);
  if (carried || freshHalt === undefined || (plannedHalt !== undefined && freshHalt >= plannedHalt)) return { keep: all, drift: false };
  return { keep: all.filter(j => j < from || steps[j]! <= freshHalt), drift: true };
}

/** Review #3 F1: a CHAIN's dry runs — under each synthetic pattern, every entrant runs on the state the earlier entrants left under that SAME
 *  pattern (the mover's entry turning the zone's caster invisible decides the follower's hand-back before any real die). The first hand-back,
 *  or null. `run` resolves one entrant from `state` and answers its end state. */
export function dryRunChain<E, S>(patterns: readonly string[], entrants: readonly E[], start: S,
  run: (entrant: E, pattern: string, state: S) => { handBack: string } | { state: S }): string | null {
  for (const pattern of patterns) {
    let state = start;
    for (const entrant of entrants) {
      const step = run(entrant, pattern, state);
      if ('handBack' in step) return step.handBack;
      state = step.state;
    }
  }
  return null;
}

/** F5: every plan's hand-backs, decided on synthetic dice, BEFORE any plan draws a real die — the first hand-back, or null. */
export function preflightAll<T>(items: readonly T[], preflight: (item: T) => string | null): { item: T; reason: string } | null {
  for (const item of items) {
    const reason = preflight(item);
    if (reason !== null) return { item, reason };
  }
  return null;
}

/** F4: a compound follower's CARRIED path — the committed route shifted by where the follower stood, starting where it stood and ending at its
 *  landing (the path the zone classifier and its prefilter both judge). */
export function carriedRoute(route: ReadonlyArray<readonly number[]>, origin: { x: number; y: number },
  member: { before: { x: number; y: number }; after: { x: number; y: number } }): number[][] {
  const out = route.map(p => [Number(p[0]) + member.before.x - origin.x, Number(p[1]) + member.before.y - origin.y]);
  if (!out.length) return [[member.before.x, member.before.y], [member.after.x, member.after.y]];
  out[0] = [member.before.x, member.before.y];
  if (out.length === 1) out.push([member.after.x, member.after.y]); else out[out.length - 1] = [member.after.x, member.after.y];
  return out;
}
